import { isValidLVGLProject, normalizeFilePathForComparison } from '@lved/shared-core';
import type { BasicPlatformApi } from '@lved/shared-core/types';
import type { FSNode } from '@lved/shared-core/types/fs-types';
import type {
    DirectoryData,
    DirectoryRefreshCallback,
    DirectoryRefreshPayload,
    IPlatformApi,
    Result
} from '@lved/shared-core/types/window-types';
import type { FileStoreState, useFileStore } from '@lved/ui/store/fileStore';
import type { usePreviewStore } from '@lved/ui/store/previewStore';
import type { UIStoreState, useUIStore } from '@lved/ui/store/uiStore';
import { TheiaPlatformApiContribution } from '@theia-lved-core/browser/platform/theia-platform-api-contribution';
import { runtimeUrlForProject } from '@theia-lved-core/browser/platform/runtime-url';
import { FrontendApplicationContribution } from '@theia/core/lib/browser';
import { Disposable, DisposableCollection } from '@theia/core/lib/common/disposable';
import { Path } from '@theia/core/lib/common/path';
import { inject, injectable } from '@theia/core/shared/inversify';
import { EditorManager } from '@theia/editor/lib/browser';
import { WorkspaceService } from '@theia/workspace/lib/browser/workspace-service';

/** Theia URIs and Windows paths → stable forward-slash keys (matches file store / API expectations). */
const toComparableFsPath = (path: string | undefined): string | undefined => {
    if (!path) return undefined;
    try {
        const raw: string = path.startsWith('file://') ? decodeURIComponent(new URL(path).pathname) : path;
        return normalizeFilePathForComparison(raw);
    } catch {
        return normalizeFilePathForComparison(path);
    }
};

type UiModule = {
    useFileStore: typeof useFileStore;
    usePreviewStore: typeof usePreviewStore;
    useUIStore: typeof useUIStore;
};

@injectable()
export class FileStoreSyncContribution implements FrontendApplicationContribution {
    @inject(WorkspaceService)
    protected readonly workspaceService!: WorkspaceService;

    @inject(EditorManager)
    protected readonly editorManager!: EditorManager;

    @inject(TheiaPlatformApiContribution)
    protected readonly platformApiContribution!: TheiaPlatformApiContribution;

    protected readonly toDispose: DisposableCollection = new DisposableCollection();

    async onStart(): Promise<void> {
        await this.attachDirectoryRefreshSync();
        await this.attachProjectPathRuntimeSync();
        this.scheduleFileStoreSync();
        this.toDispose.push(
            this.workspaceService.onWorkspaceChanged(() => {
                // Existing file-store/editor paths can still point at the previous project after File > Open.
                this.scheduleFileStoreSync(true);
            }) as unknown as Disposable
        );
    }

    protected async attachProjectPathRuntimeSync(): Promise<void> {
        const module: UiModule = (await import('@lved/ui')) as unknown as UiModule;
        const syncRuntimePath = (projectPath: string | null): void => {
            module.usePreviewStore
                .getState()
                .setRuntimeScriptPath(projectPath ? runtimeUrlForProject(projectPath) : '');
        };

        syncRuntimePath(module.useFileStore.getState().projectPath);
        this.toDispose.push(
            Disposable.create(
                module.useFileStore.subscribe((state: FileStoreState) => state.projectPath, syncRuntimePath)
            )
        );
    }

    onStop(): void {
        this.toDispose.dispose();
    }

    protected scheduleFileStoreSync(useWorkspaceRootsOnly: boolean = false): void {
        window.setTimeout(() => {
            this.syncFileStoreWithWorkspace(useWorkspaceRootsOnly).catch((error: unknown) => {
                console.warn('Failed to sync LVGL file store with Theia workspace:', error);
            });
        }, 0);
    }

    protected async attachDirectoryRefreshSync(): Promise<void> {
        const platformApi: IPlatformApi = await this.platformApiContribution.apiReady;
        const module: UiModule = (await import('@lved/ui')) as unknown as UiModule;

        const handleRefreshDirectory: DirectoryRefreshCallback = (_event: unknown, files: DirectoryRefreshPayload) => {
            console.debug(`[file-store-sync-contribution] handleRefreshDirectory ${files}`);
            const refreshedFiles: FSNode[] = this.extractRefreshFiles(files);
            if (!refreshedFiles.length) return;
            module.useFileStore.getState().setDirectoryData(refreshedFiles);
        };

        platformApi.onRefreshDirectory(handleRefreshDirectory);

        this.toDispose.push(
            Disposable.create(() => platformApi.removeRefreshDirectoryListener(handleRefreshDirectory))
        );
    }

    protected async syncFileStoreWithWorkspace(useWorkspaceRootsOnly: boolean = false): Promise<void> {
        await this.workspaceService.ready;
        const platformApi: BasicPlatformApi = await this.platformApiContribution.apiReady;
        const module: UiModule = (await import('@lved/ui')) as unknown as UiModule;
        const fileStore: FileStoreState = module.useFileStore.getState();

        const projectPath: string | undefined = await this.resolveProjectPath(
            module,
            platformApi,
            useWorkspaceRootsOnly
        );
        if (!projectPath) {
            const uiStore: UIStoreState = module.useUIStore.getState();

            if (!uiStore.showLaunchScreenOnStart) uiStore.setShowLaunchScreenOnStart(true);

            if (fileStore.projectPath !== null) await fileStore.switchProjectWithUnsavedCheck(null, []);

            return;
        }

        if (useWorkspaceRootsOnly && toComparableFsPath(fileStore.projectPath ?? undefined) === projectPath) return;

        const directoryData: DirectoryData | null = await platformApi.getDirectoryData(projectPath);
        if (!directoryData) return;

        const validation: Result = isValidLVGLProject(directoryData);
        if (
            !validation.success &&
            (useWorkspaceRootsOnly || toComparableFsPath(fileStore.projectPath ?? undefined) !== projectPath)
        ) {
            await this.showInvalidProjectError(validation.error);
            return;
        }

        if (fileStore.projectPath !== projectPath) {
            const switched: boolean = await fileStore.switchProjectWithUnsavedCheck(
                projectPath,
                directoryData.files,
                projectPath.startsWith('/online-preview/') ? { validateLicense: false } : undefined
            );
            if (switched) {
                const uiStore: UIStoreState = module.useUIStore.getState();
                if (uiStore.showLaunchScreenOnStart) uiStore.setShowLaunchScreenOnStart(false);
                module.usePreviewStore.getState().setRuntimeScriptPath(runtimeUrlForProject(projectPath));
            }
            return;
        }

        const uiStore: UIStoreState = module.useUIStore.getState();
        if (uiStore.showLaunchScreenOnStart) uiStore.setShowLaunchScreenOnStart(false);
        module.usePreviewStore.getState().setRuntimeScriptPath(runtimeUrlForProject(projectPath));
        fileStore.setDirectoryData(directoryData.files as FSNode[]);
    }

    protected extractRefreshFiles(files: DirectoryRefreshPayload): FSNode[] {
        if (!files) return [];
        if (!Array.isArray(files)) return files.files ?? [];

        return files.flatMap((entry: DirectoryData | FSNode): FSNode[] =>
            this.isDirectoryData(entry) ? entry.files : [entry]
        );
    }

    protected isDirectoryData(value: DirectoryData | FSNode): value is DirectoryData {
        return 'files' in value;
    }

    protected async resolveProjectPath(
        module: UiModule,
        platformApi: BasicPlatformApi,
        useWorkspaceRootsOnly: boolean = false
    ): Promise<string | undefined> {
        if (!useWorkspaceRootsOnly) {
            const storePath: string | null = module.useFileStore.getState().projectPath;
            if (storePath) {
                return toComparableFsPath(storePath) ?? storePath;
            }

            const editorPath: string | undefined = toComparableFsPath(
                this.editorManager.currentEditor?.getResourceUri()?.path.toString()
            );
            const fromGlobals: string | undefined = this.getProjectPathFromGlobalsFile(editorPath);
            if (fromGlobals) {
                return fromGlobals;
            }
        }

        for (const root of this.workspaceService.tryGetRoots()) {
            const candidate: string | undefined = toComparableFsPath(root.resource.path.toString());
            if (candidate) {
                return candidate;
            }
        }

        const fallbackRootPath: string | undefined = toComparableFsPath(
            this.workspaceService.tryGetRoots()[0]?.resource.path.toString()
        );
        if (!fallbackRootPath) return undefined;

        const fallbackRootData: DirectoryData | null = await platformApi.getDirectoryData(fallbackRootPath);
        return fallbackRootData ? fallbackRootPath : undefined;
    }

    protected async showInvalidProjectError(error?: string | null): Promise<void> {
        const api: IPlatformApi = await this.platformApiContribution.apiReady;
        await api.showErrorMessageBox('Invalid Project', error ?? 'This folder is not a valid LVGL UI project.');
    }

    protected getProjectPathFromGlobalsFile(filePath: string | undefined): string | undefined {
        const comparable: string | undefined = toComparableFsPath(filePath);
        if (!comparable || !comparable.toLowerCase().endsWith('/globals.xml')) {
            return undefined;
        }
        const path: Path = new Path(comparable);
        if (!path.hasDir) {
            return undefined;
        }
        return normalizeFilePathForComparison(path.dir.toString());
    }
}
